import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, AlertIcon, Box, Button, FormControl, FormLabel, Heading, Image,
  Input, Stack, Text,
} from '@chakra-ui/react';
import { useRequestPasswordResetMutation } from '../redux/services/auth';

export default function ForgotPasswordPage() {
  const [requestReset, { isLoading }] = useRequestPasswordResetMutation();
  const [sent, setSent] = useState(false);
  const [sentTo, setSentTo] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const formData = new FormData(e.currentTarget);
    const email = String(formData.get('email') || '');
    try {
      await requestReset({ email }).unwrap();
      setSentTo(email);
      setSent(true);
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to send reset email');
    }
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="stretch"
      justifyContent="center"
      bgGradient="linear(180deg, #0F0B1F 0%, #1A0F2E 40%, #2E1240 100%)"
      color="whiteAlpha.900"
      px={{ base: 5, md: 8 }}
      py={{ base: 8, md: 12 }}
      position="relative"
      overflow="hidden"
    >
      {/* Ambient glow — mirrors LoginPage so forgot-password lives in
          the same visual world as the auth surface it belongs to. */}
      <Box
        position="absolute" top="-140px" left="50%" transform="translateX(-50%)"
        w="520px" h="520px"
        bg="radial-gradient(circle, rgba(233,74,143,0.32) 0%, rgba(233,74,143,0) 65%)"
        pointerEvents="none" aria-hidden
      />
      <Box
        position="absolute" bottom="-180px" right="-140px"
        w="480px" h="480px"
        bg="radial-gradient(circle, rgba(115,84,255,0.28) 0%, rgba(115,84,255,0) 65%)"
        pointerEvents="none" aria-hidden
      />

      <Stack
        w="full" maxW="440px" spacing={7} justify="center"
        position="relative" zIndex={1}
      >
        <Stack spacing={4} align="center" textAlign="center">
          <Image src="/logo/logo_single.png" alt="MixGenie" boxSize="64px" borderRadius="14px" />
          <Heading
            as="h1"
            fontSize={{ base: '28px', md: '32px' }}
            fontWeight={800}
            lineHeight="1.1"
            letterSpacing="-0.01em"
            style={{ textWrap: 'balance' as any }}
          >
            Reset your password
          </Heading>
          <Text fontSize={{ base: 'sm', md: 'md' }} color="whiteAlpha.700" maxW="360px">
            Enter the email you use for MixGenie and we'll send you a link to set a new password.
          </Text>
        </Stack>

        <Stack
          spacing={4}
          bg="rgba(20, 14, 36, 0.7)"
          backdropFilter="blur(18px)"
          border="1px solid"
          borderColor="whiteAlpha.200"
          borderRadius="20px"
          p={{ base: 6, md: 7 }}
          boxShadow="0 24px 60px rgba(0,0,0,0.45)"
        >
          {sent ? (
            // Deliberately phrased so the user understands they need to
            // check their inbox and that Google-federated accounts won't
            // get an email (their sign-in is handled by Google — same
            // tripwire as the Allen/Oct-24 ticket). Doesn't disclose
            // whether the email exists in our DB — anti-enumeration
            // posture matches the backend's canned "if your email
            // exists" response.
            <Stack spacing={3}>
              <Alert status="success" borderRadius="12px" bg="rgba(72,187,120,0.15)" color="whiteAlpha.900" border="1px solid" borderColor="rgba(72,187,120,0.4)">
                <AlertIcon color="green.300" />
                <Box>
                  <Text fontWeight={700} mb={0.5}>Check your inbox</Text>
                  <Text fontSize="sm" color="whiteAlpha.800">
                    If <strong>{sentTo}</strong> is on MixGenie we've emailed a reset link. It may take a minute to arrive — check spam too.
                  </Text>
                </Box>
              </Alert>
              <Text fontSize="xs" color="whiteAlpha.600" textAlign="center">
                Signed up with Google and don't see an email?{' '}
                <Link to="/login" style={{ color: '#f0abfc', textDecoration: 'underline' }}>
                  Sign in with Google
                </Link>{' '}
                instead — Google handles the password for you.
              </Text>
            </Stack>
          ) : (
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {error && (
                  <Alert status="error" borderRadius="10px" bg="red.900" color="white" border="1px solid" borderColor="red.600">
                    <AlertIcon color="red.200" />
                    {error}
                  </Alert>
                )}

                <FormControl isRequired>
                  <FormLabel fontSize="xs" letterSpacing="0.08em" textTransform="uppercase" color="whiteAlpha.700" mb={1}>
                    Email
                  </FormLabel>
                  <Input
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    bg="whiteAlpha.100"
                    border="1px solid"
                    borderColor="whiteAlpha.200"
                    color="whiteAlpha.900"
                    _hover={{ borderColor: 'whiteAlpha.400' }}
                    _focus={{ borderColor: 'pink.300', bg: 'whiteAlpha.200', boxShadow: '0 0 0 1px rgba(233,74,143,0.6)' }}
                    _placeholder={{ color: 'whiteAlpha.400' }}
                    borderRadius="12px"
                    size="lg"
                    fontSize="md"
                    autoComplete="email"
                    autoFocus
                  />
                </FormControl>

                <Button
                  type="submit"
                  isLoading={isLoading}
                  size="lg"
                  w="full"
                  bgGradient="linear(90deg, #ec4899 0%, #a855f7 100%)"
                  color="white"
                  fontWeight={700}
                  _hover={{ bgGradient: 'linear(90deg, #f472b6 0%, #c084fc 100%)' }}
                  _active={{ opacity: 0.9 }}
                  borderRadius="12px"
                  data-cta="auth:reset-request"
                >
                  Send reset link
                </Button>
              </Stack>
            </form>
          )}

          <Text fontSize="sm" textAlign="center" color="whiteAlpha.700" mt={2}>
            <Link to="/login" style={{ color: '#f0abfc' }}>
              ← Back to sign in
            </Link>
          </Text>
        </Stack>
      </Stack>
    </Box>
  );
}
